import React, { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../common/types';
import { FieldTooltipContent } from '../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../common/utils/useRegisterTooltipContentProvider';

import type { singleLineTextField_updateWorkItemFieldsActionRenderer$key } from './__generated__/singleLineTextField_updateWorkItemFieldsActionRenderer.graphql';

const messages = defineMessages({
	updateAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.single-line-text-field.update-action',
		defaultMessage: 'Update {fieldName}',
		description: 'Label for the change action in the single line text field',
	},
	clearAction: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.single-line-text-field.clear-action',
		defaultMessage: 'Clear {fieldName}',
		description: 'Label for the clear action in the single line text field',
	},
});

export type Props = {
	queryRef: singleLineTextField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const SingleLineTextField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment singleLineTextField_updateWorkItemFieldsActionRenderer on JiraSingleLineTextField {
				fieldId
				name
				text
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === action?.data?.fieldKey;

	const isUpdatingAction = Boolean(action?.data?.fieldValue);

	const toolTipContentProvider = useCallback(
		() => (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				current={data?.text}
				suggested={action?.data?.fieldValue ? String(action.data.fieldValue) : undefined}
			/>
		),
		[action.data.fieldValue, action.data?.reasoning, data?.name, data?.text],
	);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: isFieldRenderable,
	});

	if (!data?.fieldId || !data?.name || data?.fieldId !== action.data.fieldKey) {
		return null;
	}

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.single-line-text-field"
		>
			{formatMessage(isUpdatingAction ? messages.updateAction : messages.clearAction, {
				fieldName: data?.name?.toLowerCase(),
			})}
		</Text>
	);
};
